import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { slurpGroups } from "../../lib/slurpGroupsApi";
import { IconGlobe, IconTelegram, IconX, IconChart, IconUsers } from "./icons";
import ImagePicker from "./ImagePicker";
import LikeButton from "./LikeButton";
import MembersModal from "./HolderGroupMembers";
import RulesEditor from "./RulesEditor";
import MinHoldInput from "./MinHoldInput";
import { useConfirmDialog } from "./ConfirmDialog";
import Linkify from "./Linkify";
import { HolderRanksContext, SlurpLoader, AuthorBlock } from "./HolderGroupShared";
import TokenLiveFeed from "./TokenLiveFeed";

// Re-export so existing import paths (e.g. from HolderGroupThread which
// used to grab AuthorBlock from this module) keep working without
// touching every consumer.
export { HolderRanksContext, SlurpLoader, AuthorBlock };

// Tiny inline lock — slurp doesn't have one in icons.js yet, and the
// emoji 🔒 looks out of place next to all the SVG icons everywhere else.
const IconLock = (p) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const fmtMembers = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

// Compact number for token amounts (1.2M, 2.5B, 1T). Strips trailing
// .0 so "2M" reads cleaner than "2.0M".
const fmtCompact = (n) => {
  const v = Number(n || 0);
  const fmt = (x, suffix) => {
    const r = x.toFixed(1);
    return (r.endsWith(".0") ? r.slice(0, -2) : r) + suffix;
  };
  if (v >= 1e12) return fmt(v / 1e12, "T");
  if (v >= 1e9) return fmt(v / 1e9, "B");
  if (v >= 1e6) return fmt(v / 1e6, "M");
  if (v >= 1e3) return fmt(v / 1e3, "K");
  return String(Math.round(v));
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const truncWallet = (w) => (w && w.length > 8 ? `${w.slice(0, 4)}…${w.slice(-4)}` : w || "");


export default function HolderGroupPage() {
  const { mint } = useParams();
  const navigate = useNavigate();
  const wallet = useWallet();

  const [group, setGroup] = useState(null);
  const [threads, setThreads] = useState([]);
  const [membership, setMembership] = useState({ member: false, role: null, banned: false });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // newest | oldest | replies | reactions — applied to the non-pinned
  // thread list only. Pinned threads always stay grouped at the top
  // in their pinned-section order (creator's choice).
  const [sortMode, setSortMode] = useState("newest");
  const [showEdit, setShowEdit] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [holderRanks, setHolderRanks] = useState({ ranks: {}, total: 0 });
  const { confirm, alert: showAlert, dialog } = useConfirmDialog();
  const PAGE_SIZE = 20;

  const refresh = useCallback(async () => {
    try {
      const viewer = wallet?.publicKey?.toBase58();
      const [g, { threads: ts }] = await Promise.all([
        slurpGroups.get(mint),
        slurpGroups.threads(mint, { viewer, limit: PAGE_SIZE, offset: 0 }),
      ]);
      setGroup(g.group);
      setThreads(ts);
      setHasMore(ts.length === PAGE_SIZE);
      if (wallet?.publicKey) {
        const m = await slurpGroups.membership(mint, wallet.publicKey.toBase58());
        setMembership(m);
      } else {
        setMembership({ member: false, role: null, banned: false });
      }
    } catch (e) {
      setErr(e?.message || "Couldn't load group");
    } finally {
      setLoading(false);
    }
  }, [mint, wallet?.publicKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const viewer = wallet?.publicKey?.toBase58();
      const { threads: more } = await slurpGroups.threads(mint, {
        viewer, limit: PAGE_SIZE, offset: threads.length,
      });
      // Append, dedup by id (a new thread posted between fetches could
      // shift the page boundary).
      setThreads((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        return [...prev, ...more.filter((t) => !seen.has(t.id))];
      });
      setHasMore(more.length === PAGE_SIZE);
    } catch (e) {
      // Non-fatal — user can retry.
      console.warn("[group] load more failed:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [mint, wallet?.publicKey, threads.length, loadingMore, hasMore]);

  useEffect(() => { refresh(); }, [refresh]);

  // Fetch holder ranks for the group's token once on mount + every 5 min
  // while open. Gives the AuthorBlock the data it needs to render TOP-10
  // / TOP-100 / TOP-1% badges.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      slurpGroups.holderRanks(mint, { top: 200 })
        .then((r) => { if (!cancelled) setHolderRanks({ ranks: r.ranks || {}, total: r.total || 0 }); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 5 * 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [mint]);

  const isAdmin = membership.role === "creator" || membership.role === "admin";

  const handleJoin = useCallback(async () => {
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    setBusy(true);
    try {
      await slurpGroups.join({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
      });
      await refresh();
    } catch (e) {
      // The backend attaches the parsed JSON body to e.body. For known
      // error codes show a friendly version; otherwise fall back to the
      // raw message.
      const body = e?.body || {};
      if (body.error === "insufficient_holding") {
        const sym = group?.symbol ? `$${group.symbol}` : "tokens";
        showAlert(
          `Sorry, you don't hold enough ${sym} to join. You need ${fmtCompact(body.required)}, you have ${fmtCompact(body.have)}.`,
          { title: "Not enough to join" }
        );
      } else if (body.error === "banned") {
        showAlert("You've been banned from this group.", { title: "Banned" });
      } else if (body.error === "group_disabled") {
        showAlert("This group has been disabled by an admin.", { title: "Group closed" });
      } else {
        showAlert(e?.message || "Couldn't join. Try again in a sec.");
      }
    } finally {
      setBusy(false);
    }
  }, [mint, wallet, refresh, showAlert, group?.symbol]);

  const handleLeave = useCallback(async () => {
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    const ok = await confirm("You can rejoin later if you still hold the tokens.", {
      title: "Leave group?", confirmLabel: "Leave", danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await slurpGroups.leave({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
      });
      await refresh();
    } catch (e) {
      showAlert(e?.message || "Couldn't leave");
    } finally {
      setBusy(false);
    }
  }, [mint, wallet, refresh, confirm, showAlert]);

  if (loading) {
    return <SlurpLoader label="Loading group" />;
  }
  if (err || !group) {
    return (
      <div className="pm-group__error">
        <div className="pm-group__error-text">{err || "Group not found"}</div>
        <Link to={`/token/${mint}`} className="pm-group__error-back">← Back to token</Link>
      </div>
    );
  }

  return (
    <HolderRanksContext.Provider value={holderRanks}>
    <div className="pm-group">
      {dialog}
      <header
        className="pm-group__hero"
        style={group.banner_url ? { backgroundImage: `url(${group.banner_url})` } : undefined}
      >
        <div className="pm-group__hero-overlay" />
        <div className="pm-group__hero-socials">
          {group.token_website && (
            <a className="pm-group__hero-social" href={group.token_website} target="_blank" rel="noreferrer" aria-label="Website">
              <IconGlobe />
            </a>
          )}
          {group.token_telegram && (
            <a className="pm-group__hero-social" href={group.token_telegram} target="_blank" rel="noreferrer" aria-label="Telegram">
              <IconTelegram />
            </a>
          )}
          {group.token_twitter && (
            <a className="pm-group__hero-social" href={group.token_twitter} target="_blank" rel="noreferrer" aria-label="Twitter / X">
              <IconX />
            </a>
          )}
          <a className="pm-group__hero-social pm-group__hero-social--dex"
             href={`https://dexscreener.com/solana/${mint}`}
             target="_blank" rel="noreferrer" aria-label="DexScreener">
            <img src="/images/png/dex-screener-seeklogo.png" alt="" />
          </a>
        </div>
        <div className="pm-group__hero-content">
          <div className="pm-group__hero-back-row">
            <Link to="/groups" className="pm-group__hero-back">← Communities</Link>
            <Link to={`/token/${mint}`} className="pm-group__hero-back">← ${group.symbol}</Link>
          </div>
          {(() => {
            const supplyWhole = group.token_supply_raw && group.decimals != null
              ? Number(group.token_supply_raw) / 10 ** Number(group.decimals)
              : 0;
            const minTokens = Number(group.min_tokens_to_join) || 0;
            const pct = supplyWhole > 0 ? (minTokens / supplyWhole) * 100 : 0;
            const isWhale = pct >= 1;
            const tierLabel = isWhale ? "WHALE CLUB" : "HOLDERS";
            return (
              <div className="pm-group__hero-crest">
                {group.token_image_url && (
                  <div className={`pm-group__hero-pfp-wrap${isWhale ? " is-whale" : ""}`}>
                    <img src={group.token_image_url} alt="" className="pm-group__hero-pfp" />
                  </div>
                )}
                <div className="pm-group__hero-title-block">
                  <div className={`pm-group__hero-kicker${isWhale ? " is-whale" : ""}`}>
                    <IconUsers className="pm-group__hero-kicker-icon" aria-hidden="true" />
                    {tierLabel}
                    <span className="pm-group__hero-kicker-sep">·</span>
                    <span className="pm-group__hero-kicker-count">{fmtMembers(group.member_count)}</span>
                    <button
                      type="button"
                      className="pm-group__hero-kicker-members"
                      onClick={() => setShowMembers(true)}
                    >
                      {Number(group.member_count) === 1 ? "MEMBER" : "MEMBERS"}
                    </button>
                    {membership.member && (
                      <>
                        <span className="pm-group__hero-kicker-sep">·</span>
                        <span className="pm-group__hero-kicker-you">YOU'RE IN</span>
                      </>
                    )}
                  </div>
                  <span className="pm-group__hero-ticker">${group.symbol}</span>
                  {group.description && (
                    <span className="pm-group__hero-desc">{group.description}</span>
                  )}
                  {!membership.member && (
                    <div className="pm-group__hero-cta">
                      <button
                        type="button"
                        className="pm-group__hero-cta-btn pm-group__hero-cta-btn--join"
                        disabled={busy || !wallet?.publicKey}
                        onClick={handleJoin}
                      >
                        {!wallet?.publicKey
                          ? "connect wallet to join"
                          : membership.banned
                            ? "you're banned"
                            : busy
                              ? "joining…"
                              : `join the ${isWhale ? "whale club" : "community"}`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        <div className="pm-group__hero-corner">
          {membership.member && membership.role !== "creator" && (
            <button
              type="button"
              className="pm-group__hero-corner-btn pm-group__hero-corner-btn--leave"
              disabled={busy}
              onClick={handleLeave}
            >
              leave
            </button>
          )}
          {(membership.role === "creator" || membership.role === "admin") && (
            <button
              type="button"
              className="pm-group__hero-corner-btn"
              onClick={() => setShowEdit(true)}
            >
              edit group
            </button>
          )}
        </div>
      </header>

      <div className="pm-group__body">
        {membership.member && group.rules && (() => {
          const rulesList = group.rules
            .split("\n")
            .map((r) => r.trim())
            .filter(Boolean);
          if (rulesList.length === 0) return null;
          return (
            <section className={`pm-group__rules-card${rulesOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="pm-group__rules-card-toggle"
                onClick={() => setRulesOpen((v) => !v)}
                aria-expanded={rulesOpen}
              >
                <span className="pm-group__rules-card-tag">GROUP RULES</span>
                <span className="pm-group__rules-card-count">
                  {rulesList.length} {rulesList.length === 1 ? "rule" : "rules"}
                </span>
                <span className="pm-group__rules-card-chev" aria-hidden="true">
                  {rulesOpen ? "▾" : "▸"}
                </span>
              </button>
              {rulesOpen && (
                <>
                  <ol className="pm-group__rules-card-list">
                    {rulesList.map((r, i) => (
                      <li key={i}>
                        <span className="pm-group__rules-card-num">{String(i + 1).padStart(2, "0")}</span>
                        <span className="pm-group__rules-card-text">{r}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="pm-group__rules-card-foot">
                    Breaking these can get you removed without warning.
                  </p>
                </>
              )}
            </section>
          );
        })()}
        <TokenLiveFeed mint={mint} feeMode={group.fee_mode} symbol={group.symbol} />
        <div className="pm-group__toolbar">
          <div className="pm-group__toolbar-actions">
            {isAdmin && (
              <button
                type="button"
                className="pm-group__sticker pm-group__sticker--butter"
                onClick={() => { setShowPoll((v) => !v); setShowCompose(false); }}
              >
                {showPoll ? "× cancel poll" : "+ new poll"}
              </button>
            )}
            {membership.member && !membership.banned && (
              <button
                type="button"
                className="pm-group__sticker pm-group__sticker--accent"
                onClick={() => { setShowCompose((v) => !v); setShowPoll(false); }}
              >
                {showCompose ? "× cancel" : "+ new thread"}
              </button>
            )}
          </div>
        </div>

        {showCompose && (
          <ComposeThread
            mint={mint}
            wallet={wallet}
            onPosted={async () => {
              // Stay on the group page so the user sees their post
              // appear at the top of the feed instead of being yanked
              // into the thread detail.
              setShowCompose(false);
              await refresh();
            }}
          />
        )}
        {showPoll && (
          <ComposePoll
            mint={mint}
            wallet={wallet}
            onPosted={async () => {
              setShowPoll(false);
              await refresh();
            }}
          />
        )}

        {!membership.member ? (
          <div className="pm-group__locked">
            <div className="pm-group__locked-title"><IconLock /> Members only</div>
            <p className="pm-group__locked-text">
              {membership.banned
                ? "You've been banned from this group."
                : Number(group.min_tokens_to_join) > 0
                  ? `Hold at least ${fmtCompact(group.min_tokens_to_join)} $${group.symbol} and join above to read posts and polls.`
                  : `Join the group above to read posts and polls.`}
            </p>
          </div>
        ) : threads.length === 0 ? (
          <div className="pm-group__empty">No threads yet — be the first.</div>
        ) : (() => {
          // Split into pinned (creator's curated top) + regular (sorted
          // by user pill choice). Two visually distinct sections instead
          // of one mixed list with an inline divider.
          const pinned = threads.filter((t) => t.pinned);
          const regular = threads.filter((t) => !t.pinned).slice();
          regular.sort((a, b) => {
            switch (sortMode) {
              case "oldest":
                return new Date(a.created_at) - new Date(b.created_at);
              case "replies":
                return (b.comment_count || 0) - (a.comment_count || 0);
              case "reactions":
                return ((b.like_count || 0) - (b.dislike_count || 0))
                     - ((a.like_count || 0) - (a.dislike_count || 0));
              case "newest":
              default:
                return new Date(b.created_at) - new Date(a.created_at);
            }
          });
          const sortPills = [
            ["newest", "Newest"],
            ["replies", "Most replies"],
            ["reactions", "Most reactions"],
            ["oldest", "Oldest"],
          ];
          const toggleCardPin = async (e, t) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              await slurpGroups.pinThread({
                mint,
                wallet: wallet.publicKey,
                signMessage: wallet.signMessage,
                threadId: t.id,
                pinned: !t.pinned,
              });
              await refresh();
            } catch (err) { showAlert?.(err?.message || "Pin failed"); }
          };
          const renderThreadCard = (t) => (
              <li key={t.id}>
                <Link to={`/groups/${mint}/thread/${t.id}`} className={`pm-group__thread${t.pinned ? " is-pinned" : ""}${t.poll_options ? " is-poll" : ""}`}>
                  {isAdmin && (
                    <button
                      type="button"
                      className={`pm-group__thread-pin-btn${t.pinned ? " is-pinned" : ""}`}
                      onClick={(e) => toggleCardPin(e, t)}
                      title={t.pinned ? "Unpin this thread" : "Pin this thread"}
                      aria-label={t.pinned ? "Unpin" : "Pin"}
                    >
                      📌
                    </button>
                  )}
                  <div className="pm-group__thread-badges">
                    {t.pinned && <span className="pm-group__pin-badge">PINNED</span>}
                    {t.poll_options && (
                      <span className="pm-group__poll-badge">
                        <IconChart />
                        POLL · {t.poll_vote_total || 0} {Number(t.poll_vote_total) === 1 ? "vote" : "votes"}
                      </span>
                    )}
                  </div>
                  <h3 className="pm-group__thread-title">{t.title}</h3>
                  {t.body && (
                    <p className="pm-group__thread-snippet">
                      <Linkify text={t.body.slice(0, 240) + (t.body.length > 240 ? "…" : "")} />
                    </p>
                  )}
                  <div className="pm-group__thread-meta">
                    <AuthorBlock
                      wallet={t.author}
                      username={t.author_username}
                      imageUrl={t.author_image_url}
                      role={t.author_role}
                    />
                    <span className="pm-group__thread-meta-dot">·</span>
                    <span>{timeAgo(t.created_at)}</span>
                    <span className="pm-group__thread-meta-dot">·</span>
                    <span>{t.comment_count} {t.comment_count === 1 ? "reply" : "replies"}</span>
                    {t.last_comment_at && (
                      <>
                        <span className="pm-group__thread-meta-dot">·</span>
                        <span>last reply {timeAgo(t.last_comment_at)}</span>
                      </>
                    )}
                    <span className="pm-group__thread-meta-spacer" />
                    <LikeButton
                      reaction={t.viewer_reaction}
                      likeCount={t.like_count}
                      dislikeCount={t.dislike_count}
                      disabled={!membership.member}
                      onToggle={(kind) => slurpGroups.reactThread({
                        mint,
                        wallet: wallet.publicKey,
                        signMessage: wallet.signMessage,
                        threadId: t.id,
                        kind,
                      })}
                    />
                  </div>
                </Link>
              </li>
          );
          return (
            <>
              {pinned.length > 0 && (
                <section className="pm-group__section-block">
                  <div className="pm-group__section-head-row">
                    <h3 className="pm-group__section-head pm-group__section-head--pinned">
                      📌 Pinned
                    </h3>
                    {pinned.length > 3 && (
                      <div className="pm-group__pinned-nav">
                        <button
                          type="button"
                          className="pm-group__pinned-nav-btn"
                          aria-label="Scroll pinned left"
                          onClick={() => {
                            const el = document.getElementById("pm-group-pinned-scroll");
                            el?.scrollBy({ left: -304, behavior: "smooth" });
                          }}
                        >‹</button>
                        <button
                          type="button"
                          className="pm-group__pinned-nav-btn"
                          aria-label="Scroll pinned right"
                          onClick={() => {
                            const el = document.getElementById("pm-group-pinned-scroll");
                            el?.scrollBy({ left: 304, behavior: "smooth" });
                          }}
                        >›</button>
                      </div>
                    )}
                  </div>
                  {pinned.length > 3 ? (
                    <ul
                      id="pm-group-pinned-scroll"
                      className="pm-group__threads pm-group__threads--carousel"
                    >
                      {pinned.map(renderThreadCard)}
                    </ul>
                  ) : (
                    <ul className="pm-group__threads">
                      {pinned.map(renderThreadCard)}
                    </ul>
                  )}
                </section>
              )}
              {regular.length > 0 && (
                <section className="pm-group__section-block">
                  <div className="pm-group__section-head-row">
                    <h3 className="pm-group__section-head">Threads</h3>
                    <div className="pm-group__sort-pills" role="tablist" aria-label="Sort threads">
                      {sortPills.map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={sortMode === key}
                          className={`pm-group__sort-pill${sortMode === key ? " is-active" : ""}`}
                          onClick={() => setSortMode(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ul className="pm-group__threads">
                    {regular.map(renderThreadCard)}
                  </ul>
                </section>
              )}
            </>
          );
        })()}

        {membership.member && hasMore && (
          <div className="pm-group__loadmore">
            <button
              type="button"
              className="pm-group__sticker pm-group__sticker--cream-dark"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "loading…" : "+ load more threads"}
            </button>
          </div>
        )}
      </div>
      {showMembers && (
        <MembersModal
          mint={mint}
          wallet={wallet}
          viewerRole={membership.role}
          minTokensToJoin={Number(group.min_tokens_to_join) || 0}
          symbol={group.symbol}
          onClose={() => setShowMembers(false)}
          onMembersChanged={refresh}
        />
      )}
      {showEdit && (
        <EditGroupModal
          mint={mint}
          symbol={group.symbol}
          group={group}
          wallet={wallet}
          isCreator={membership.role === "creator"}
          onClose={() => setShowEdit(false)}
          onSaved={async () => {
            setShowEdit(false);
            await refresh();
          }}
        />
      )}
    </div>
    </HolderRanksContext.Provider>
  );
}

function EditGroupModal({ mint, symbol, group, wallet, isCreator, onClose, onSaved }) {
  const [description, setDescription] = useState(group.description || "");
  const [rules, setRules] = useState(group.rules || "");
  const [bannerUrl, setBannerUrl] = useState(group.banner_url || "");
  const [minTokens, setMinTokens] = useState(String(group.min_tokens_to_join || "0"));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    setBusy(true);
    try {
      // Strip blank lines from the rules editor before sending — the
      // editor preserves empty rows in its working state.
      const cleanedRules = rules.split("\n").map((s) => s.trim()).filter(Boolean).join("\n");
      await slurpGroups.update({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        banner_url: bannerUrl.trim() || null,
        description: description.trim() || null,
        rules: cleanedRules || null,
        // Only the creator is allowed to change the threshold; admins can
        // edit the cosmetics. Sending undefined makes the backend skip it.
        min_tokens_to_join: isCreator ? Math.max(0, Number(minTokens) || 0) : undefined,
      });
      onSaved();
    } catch (err) {
      alert(err?.message || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pm-modal__bg" onClick={onClose}>
      <div className="pm-modal pm-group__create-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pm-modal__close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 3 L11 11 M11 3 L3 11"/></svg>
        </button>
        <h2 className="pm-modal__title">Edit ${symbol} Holder Group</h2>
        <form onSubmit={submit} className="pm-group__create-form">
          <label className="pm-group__create-label">
            Description (one sentence, 30 chars max)
            <input
              type="text"
              className="pm-group__create-input"
              placeholder="One sentence about your group"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={30}
            />
            <span className="pm-group__create-hint">{description.length}/30</span>
          </label>
          <label className="pm-group__create-label">
            Group rules
            <RulesEditor value={rules} onChange={setRules} disabled={busy} />
            <span className="pm-group__create-hint">Each line is a separate rule. Shown to members on the group page.</span>
          </label>
          <label className="pm-group__create-label">
            Banner image
            <ImagePicker value={bannerUrl} onChange={setBannerUrl} prefix="groups/banners" disabled={busy} />
          </label>
          {isCreator && (
            <label className="pm-group__create-label">
              Minimum ${symbol} to join
              <MinHoldInput
                value={Number(minTokens) || 0}
                onChange={(v) => setMinTokens(String(v))}
                symbol={symbol}
                supplyWhole={group.token_supply_raw && group.decimals != null
                  ? Number(group.token_supply_raw) / 10 ** Number(group.decimals)
                  : 0}
                disabled={busy}
              />
              <span className="pm-group__create-hint">Existing members keep access; only new joiners are checked.</span>
            </label>
          )}
          <div className="pm-group__create-actions">
            <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={onClose}>
              cancel
            </button>
            <button type="submit" className="pm-group__sticker pm-group__sticker--accent" disabled={busy}>
              {busy ? "saving…" : "save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ComposePoll({ mint, wallet, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [closesIn, setClosesIn] = useState(""); // hours, blank = no close
  const [busy, setBusy] = useState(false);

  const updateOption = (i, v) => setOptions((arr) => arr.map((o, idx) => (idx === i ? v : o)));
  const addOption = () => setOptions((arr) => (arr.length >= 6 ? arr : [...arr, ""]));
  const removeOption = (i) => setOptions((arr) => (arr.length <= 2 ? arr : arr.filter((_, idx) => idx !== i)));

  const submit = async (e) => {
    e.preventDefault();
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    if (!title.trim()) return;
    const cleaned = options.map((o) => o.trim()).filter(Boolean);
    if (cleaned.length < 2) { alert("Need at least 2 options."); return; }
    let closesAt = null;
    if (closesIn && Number(closesIn) > 0) {
      closesAt = new Date(Date.now() + Number(closesIn) * 3600_000).toISOString();
    }
    setBusy(true);
    try {
      const r = await slurpGroups.postPoll({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        title: title.trim(),
        body: body.trim(),
        options: cleaned,
        closes_at: closesAt,
      });
      onPosted(r.id);
    } catch (err) {
      alert(err?.message || "Couldn't post poll");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="pm-group__compose" onSubmit={submit}>
      <input
        type="text"
        className="pm-group__compose-title"
        placeholder="Poll question"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={250}
        required
      />
      <textarea
        className="pm-group__compose-body"
        placeholder="Optional context"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={250}
        rows={2}
      />
      <div className="pm-group__poll-options-edit">
        {options.map((opt, i) => (
          <div key={i} className="pm-group__poll-option-edit">
            <input
              type="text"
              className="pm-group__compose-title pm-group__poll-option-input"
              placeholder={`Option ${i + 1}`}
              value={opt}
              onChange={(e) => updateOption(i, e.target.value)}
              maxLength={120}
            />
            {options.length > 2 && (
              <button type="button" className="pm-group__poll-option-remove" onClick={() => removeOption(i)} aria-label="Remove option">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M2 2 L10 10 M10 2 L2 10"/></svg>
              </button>
            )}
          </div>
        ))}
        {options.length < 6 && (
          <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={addOption}>
            + add option
          </button>
        )}
      </div>
      <div className="pm-group__poll-closes-row">
        <label className="pm-group__poll-closes-label">
          Close after (hours, optional):
          <input
            type="number"
            min="0"
            step="1"
            placeholder="—"
            className="pm-group__poll-closes-input"
            value={closesIn}
            onChange={(e) => setClosesIn(e.target.value)}
          />
        </label>
      </div>
      <div className="pm-group__compose-actions">
        <span className="pm-group__compose-count">{options.filter((o) => o.trim()).length}/6 options</span>
        <button
          type="submit"
          className="pm-group__sticker pm-group__sticker--butter"
          disabled={busy || !title.trim() || options.filter((o) => o.trim()).length < 2}
        >
          {busy ? "posting…" : "post poll"}
        </button>
      </div>
    </form>
  );
}

function ComposeThread({ mint, wallet, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    if (!title.trim()) return;
    setBusy(true);
    try {
      const r = await slurpGroups.postThread({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        title: title.trim(),
        body: body.trim(),
        image_url: imageUrl || null,
      });
      onPosted(r.id);
    } catch (err) {
      alert(err?.message || "Couldn't post");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="pm-group__compose" onSubmit={submit}>
      <input
        type="text"
        className="pm-group__compose-title"
        placeholder="Thread title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={250}
        required
      />
      <textarea
        className="pm-group__compose-body"
        placeholder="What's on your mind?"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={250}
        rows={4}
      />
      <ImagePicker value={imageUrl} onChange={setImageUrl} prefix="groups/threads" disabled={busy} />
      <div className="pm-group__compose-actions">
        <span className="pm-group__compose-count">{body.length}/250</span>
        <button
          type="submit"
          className="pm-group__sticker pm-group__sticker--accent"
          disabled={busy || !title.trim()}
        >
          {busy ? "posting…" : "post thread"}
        </button>
      </div>
    </form>
  );
}
